import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
	MediaUpload,
	MediaUploadCheck,
} from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	TextControl,
	ToggleControl,
	RangeControl,
	Button,
	Spinner,
	Placeholder,
	Icon,
	ColorPalette,
} from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render';
import './editor.scss';

/**
 * Skeleton placeholder shown while ServerSideRender is loading.
 *
 * @return {Element} Skeleton component.
 */
function SkeletonLoader() {
	return (
		<div className="bbpc-ft-skeleton">
			<div className="bbpc-ft-skeleton__tabs">
				<div className="bbpc-ft-skeleton__tab" />
				<div className="bbpc-ft-skeleton__tab" />
			</div>
			<div className="bbpc-ft-skeleton__grid">
				{ [ 1, 2, 3 ].map( ( i ) => (
					<div className="bbpc-ft-skeleton__card" key={ i }>
						<div className="bbpc-ft-skeleton__img" />
						<div className="bbpc-ft-skeleton__line bbpc-ft-skeleton__line--title" />
						<div className="bbpc-ft-skeleton__line bbpc-ft-skeleton__line--meta" />
					</div>
				) ) }
			</div>
		</div>
	);
}

export default function Edit( { attributes, setAttributes } ) {
	const blockProps = useBlockProps();

	/* Check if Forumax Pro is active. */
	const isProActive =
		window.bbpc_upsell_config?.is_pro_active === '1' ||
		window.bbpc_upsell_config?.is_pro_active === true;
	const {
		forum_tab_title,
		ppp,
		order,
		orderby,
		is_forum_tab_btn,
		more_txt,
		more_url,
		topics_tab_title,
		ppp2,
		order2,
		orderby2,
		is_topic_tab_btn,
		more_txt2,
		more_url2,
		forum_filter_ids,
		default_active_tab,
		columns,
		show_forum_image,
		show_forum_post_count,
		show_forum_reply_count,
		show_forum_excerpt,
		show_topic_avatar,
		show_topic_post_info,
		show_topic_tags,
		show_topic_post_reach,
		forum_image_size,
		enable_card_hover,
		no_posts_message,
		forum_tab_title_color,
		topics_tab_title_color,
		forum_tab_content_color,
		topics_tab_content_color,
		tab_active_color,
		title_hover_color,
		btn_color,
		btn_bg_color,
		card_bg_color,
		tab_font_size,
		card_title_font_size,
		meta_font_size,
		tab_padding,
		card_gap,
		section_padding,
		card_border_radius,
		btn_border_radius,
		hide_forum_image_mobile,
		hide_topic_reach_mobile,
		mobile_columns,
	} = attributes;

	const orderByOptions = [
		{ label: __( 'Date', 'bbp-core' ), value: 'date' },
		{ label: __( 'Title', 'bbp-core' ), value: 'title' },
		{ label: __( 'Modified', 'bbp-core' ), value: 'modified' },
		{ label: __( 'Menu Order', 'bbp-core' ), value: 'menu_order' },
		{ label: __( 'ID', 'bbp-core' ), value: 'ID' },
	];

	return (
		<div { ...blockProps }>
			{ /* ======== SETTINGS TAB ======== */ }
			<InspectorControls>
				{ /* -------- Forum Filter Options -------- */ }
				<PanelBody title={ __( 'Forum Filter Options', 'bbp-core' ) }>
					<TextControl
						label={ __( 'Forum Tab Title', 'bbp-core' ) }
						value={ forum_tab_title }
						onChange={ ( value ) =>
							setAttributes( { forum_tab_title: value } )
						}
					/>
					<TextControl
						label={ __( 'Show Forums', 'bbp-core' ) }
						type="number"
						value={ ppp }
						onChange={ ( value ) =>
							setAttributes( { ppp: parseInt( value ) } )
						}
						help={ __(
							'Maximum number of forums to show.',
							'bbp-core'
						) }
					/>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<RangeControl
							label={ __( 'Forum Grid Columns', 'bbp-core' ) }
							value={ isProActive ? columns : 3 }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { columns: value } );
								}
							} }
							min={ 1 }
							max={ 4 }
							disabled={ ! isProActive }
						/>
					</div>
					<SelectControl
						label={ __( 'Order', 'bbp-core' ) }
						value={ order }
						options={ [
							{ label: 'ASC', value: 'ASC' },
							{ label: 'DESC', value: 'DESC' },
						] }
						onChange={ ( value ) =>
							setAttributes( { order: value } )
						}
					/>
					<SelectControl
						label={ __( 'Order By', 'bbp-core' ) }
						value={ orderby }
						options={ orderByOptions }
						onChange={ ( value ) =>
							setAttributes( { orderby: value } )
						}
					/>
					<TextControl
						label={ __( 'Filter by Forum IDs', 'bbp-core' ) }
						value={ forum_filter_ids }
						onChange={ ( value ) =>
							setAttributes( { forum_filter_ids: value } )
						}
						help={ __(
							'Comma-separated forum IDs. Leave empty to show all.',
							'bbp-core'
						) }
					/>
					<SelectControl
						label={ __( 'Forum Image Size', 'bbp-core' ) }
						value={ forum_image_size }
						options={ [
							{
								label: __( 'Thumbnail', 'bbp-core' ),
								value: 'thumbnail',
							},
							{
								label: __( 'Medium', 'bbp-core' ),
								value: 'medium',
							},
							{
								label: __( 'Large', 'bbp-core' ),
								value: 'large',
							},
							{
								label: __( 'Full', 'bbp-core' ),
								value: 'full',
							},
						] }
						onChange={ ( value ) =>
							setAttributes( { forum_image_size: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show "View More" Button', 'bbp-core' ) }
						checked={ is_forum_tab_btn }
						onChange={ ( value ) =>
							setAttributes( { is_forum_tab_btn: value } )
						}
					/>
					{ is_forum_tab_btn && (
						<>
							<TextControl
								label={ __( 'Button Label', 'bbp-core' ) }
								value={ more_txt }
								onChange={ ( value ) =>
									setAttributes( { more_txt: value } )
								}
							/>
							<TextControl
								label={ __( 'Button URL', 'bbp-core' ) }
								value={ more_url }
								onChange={ ( value ) =>
									setAttributes( { more_url: value } )
								}
							/>
						</>
					) }
				</PanelBody>

				{ /* -------- Topic Filter Options -------- */ }
				<PanelBody
					title={ __( 'Topic Filter Options', 'bbp-core' ) }
					initialOpen={ false }
				>
					<TextControl
						label={ __( 'Topics Tab Title', 'bbp-core' ) }
						value={ topics_tab_title }
						onChange={ ( value ) =>
							setAttributes( { topics_tab_title: value } )
						}
					/>
					<TextControl
						label={ __( 'Show Topics', 'bbp-core' ) }
						type="number"
						value={ ppp2 }
						onChange={ ( value ) =>
							setAttributes( { ppp2: parseInt( value ) } )
						}
						help={ __(
							'Maximum number of topics to show.',
							'bbp-core'
						) }
					/>
					<SelectControl
						label={ __( 'Order', 'bbp-core' ) }
						value={ order2 }
						options={ [
							{ label: 'ASC', value: 'ASC' },
							{ label: 'DESC', value: 'DESC' },
						] }
						onChange={ ( value ) =>
							setAttributes( { order2: value } )
						}
					/>
					<SelectControl
						label={ __( 'Order By', 'bbp-core' ) }
						value={ orderby2 }
						options={ orderByOptions }
						onChange={ ( value ) =>
							setAttributes( { orderby2: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show "View More" Button', 'bbp-core' ) }
						checked={ is_topic_tab_btn }
						onChange={ ( value ) =>
							setAttributes( { is_topic_tab_btn: value } )
						}
					/>
					{ is_topic_tab_btn && (
						<>
							<TextControl
								label={ __( 'Button Label', 'bbp-core' ) }
								value={ more_txt2 }
								onChange={ ( value ) =>
									setAttributes( { more_txt2: value } )
								}
							/>
							<TextControl
								label={ __( 'Button URL', 'bbp-core' ) }
								value={ more_url2 }
								onChange={ ( value ) =>
									setAttributes( { more_url2: value } )
								}
							/>
						</>
					) }
				</PanelBody>

				{ /* -------- General -------- */ }
				<PanelBody
					title={ __( 'General', 'bbp-core' ) }
					initialOpen={ false }
				>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<SelectControl
							label={ __( 'Default Active Tab', 'bbp-core' ) }
							value={ isProActive ? default_active_tab : 'forum' }
							options={ [
								{
									label: __( 'Forums', 'bbp-core' ),
									value: 'forum',
								},
								{
									label: __( 'Topics', 'bbp-core' ),
									value: 'topic',
								},
							] }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { default_active_tab: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
					<TextControl
						label={ __(
							'"No Posts Found" Message',
							'bbp-core'
						) }
						value={ no_posts_message }
						onChange={ ( value ) =>
							setAttributes( { no_posts_message: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Visibility -------- */ }
				<PanelBody
					title={ __( 'Visibility', 'bbp-core' ) }
					initialOpen={ false }
				>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<p
							style={ {
								fontWeight: 600,
								marginBottom: '8px',
							} }
						>
							{ __( 'Forum Tab', 'bbp-core' ) }
						</p>
						<ToggleControl
							label={ __( 'Show Forum Image', 'bbp-core' ) }
							checked={ isProActive ? show_forum_image : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_forum_image: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Show Post Count', 'bbp-core' ) }
							checked={ isProActive ? show_forum_post_count : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( {
										show_forum_post_count: value,
									} );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Show Reply Count', 'bbp-core' ) }
							checked={ isProActive ? show_forum_reply_count : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( {
										show_forum_reply_count: value,
									} );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Show Forum Excerpt', 'bbp-core' ) }
							checked={ isProActive ? show_forum_excerpt : false }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_forum_excerpt: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<hr />
						<p
							style={ {
								fontWeight: 600,
								marginBottom: '8px',
							} }
						>
							{ __( 'Topic Tab', 'bbp-core' ) }
						</p>
						<ToggleControl
							label={ __( 'Show Author Avatar', 'bbp-core' ) }
							checked={ isProActive ? show_topic_avatar : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_topic_avatar: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Show Post Info', 'bbp-core' ) }
							checked={ isProActive ? show_topic_post_info : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( {
										show_topic_post_info: value,
									} );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Show Tags', 'bbp-core' ) }
							checked={ isProActive ? show_topic_tags : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_topic_tags: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __(
								'Show Post Reach (Voices/Replies)',
								'bbp-core'
							) }
							checked={ isProActive ? show_topic_post_reach : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( {
										show_topic_post_reach: value,
									} );
								}
							} }
							disabled={ ! isProActive }
						/>
						<hr />
						<ToggleControl
							label={ __(
								'Enable Card Hover Animation',
								'bbp-core'
							) }
							checked={ isProActive ? enable_card_hover : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { enable_card_hover: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
				</PanelBody>

				{ /* -------- Responsive -------- */ }
				<PanelBody
					title={ __( 'Responsive', 'bbp-core' ) }
					initialOpen={ false }
				>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<p className="components-base-control__help">
							{ __(
								'Controls for mobile devices (< 768px).',
								'bbp-core'
							) }
						</p>
						<RangeControl
							label={ __(
								'Mobile Columns (Forum Grid)',
								'bbp-core'
							) }
							value={ isProActive ? mobile_columns : 1 }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { mobile_columns: value } );
								}
							} }
							min={ 1 }
							max={ 2 }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __(
								'Hide Forum Image on Mobile',
								'bbp-core'
							) }
							checked={ isProActive ? hide_forum_image_mobile : false }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( {
										hide_forum_image_mobile: value,
									} );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __(
								'Hide Post Reach on Mobile',
								'bbp-core'
							) }
							checked={ isProActive ? hide_topic_reach_mobile : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( {
										hide_topic_reach_mobile: value,
									} );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
				</PanelBody>
			</InspectorControls>

			{ /* ======== STYLE TAB ======== */ }
			<InspectorControls group="styles">
				{ /* -------- Colors -------- */ }
				<PanelBody
					title={ __( 'Colors', 'bbp-core' ) }
					initialOpen={ false }
				>
					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Forums Tab Label Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ forum_tab_title_color }
						onChange={ ( value ) =>
							setAttributes( {
								forum_tab_title_color: value,
							} )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Topics Tab Label Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ topics_tab_title_color }
						onChange={ ( value ) =>
							setAttributes( {
								topics_tab_title_color: value,
							} )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Tab Active Border Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ tab_active_color }
						onChange={ ( value ) =>
							setAttributes( { tab_active_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Forums Content Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ forum_tab_content_color }
						onChange={ ( value ) =>
							setAttributes( {
								forum_tab_content_color: value,
							} )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Topics Content Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ topics_tab_content_color }
						onChange={ ( value ) =>
							setAttributes( {
								topics_tab_content_color: value,
							} )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Title Hover Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ title_hover_color }
						onChange={ ( value ) =>
							setAttributes( { title_hover_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Card Background Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ card_bg_color }
						onChange={ ( value ) =>
							setAttributes( { card_bg_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Button Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ btn_color }
						onChange={ ( value ) =>
							setAttributes( { btn_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Button Background', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ btn_bg_color }
						onChange={ ( value ) =>
							setAttributes( { btn_bg_color: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Typography -------- */ }
				<PanelBody
					title={ __( 'Typography', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __(
							'Tab Title Font Size (px)',
							'bbp-core'
						) }
						value={ tab_font_size }
						onChange={ ( value ) =>
							setAttributes( { tab_font_size: value } )
						}
						min={ 10 }
						max={ 30 }
						allowReset
					/>
					<RangeControl
						label={ __(
							'Card Title Font Size (px)',
							'bbp-core'
						) }
						value={ card_title_font_size }
						onChange={ ( value ) =>
							setAttributes( {
								card_title_font_size: value,
							} )
						}
						min={ 10 }
						max={ 30 }
						allowReset
					/>
					<RangeControl
						label={ __(
							'Meta Font Size (px)',
							'bbp-core'
						) }
						value={ meta_font_size }
						onChange={ ( value ) =>
							setAttributes( { meta_font_size: value } )
						}
						min={ 10 }
						max={ 20 }
						allowReset
					/>
				</PanelBody>

				{ /* -------- Spacing -------- */ }
				<PanelBody
					title={ __( 'Spacing', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Tab Padding (px)', 'bbp-core' ) }
						value={ tab_padding }
						onChange={ ( value ) =>
							setAttributes( { tab_padding: value } )
						}
						min={ 4 }
						max={ 30 }
						allowReset
					/>
					<RangeControl
						label={ __( 'Card Gap (px)', 'bbp-core' ) }
						value={ card_gap }
						onChange={ ( value ) =>
							setAttributes( { card_gap: value } )
						}
						min={ 0 }
						max={ 40 }
						allowReset
					/>
					<RangeControl
						label={ __(
							'Section Padding Top (px)',
							'bbp-core'
						) }
						value={ section_padding }
						onChange={ ( value ) =>
							setAttributes( { section_padding: value } )
						}
						min={ 0 }
						max={ 80 }
						allowReset
					/>
					<RangeControl
						label={ __(
							'Card Border Radius (px)',
							'bbp-core'
						) }
						value={ card_border_radius }
						onChange={ ( value ) =>
							setAttributes( {
								card_border_radius: value,
							} )
						}
						min={ 0 }
						max={ 30 }
						allowReset
					/>
					<RangeControl
						label={ __(
							'Button Border Radius (px)',
							'bbp-core'
						) }
						value={ btn_border_radius }
						onChange={ ( value ) =>
							setAttributes( { btn_border_radius: value } )
						}
						min={ 0 }
						max={ 50 }
						allowReset
					/>
				</PanelBody>
			</InspectorControls>

			{ /* -------- Block Content -------- */ }
			<ServerSideRender
				block="bbp-core/forum-tab"
				attributes={ attributes }
				LoadingResponsePlaceholder={ SkeletonLoader }
			/>
		</div>
	);
}
